import api from '../utils/axiosConfig';
import {
  PlaybookActionsStats,
  PlaybookRecommendation,
  PlaybookSession,
  PlaybookSessionStep,
  PlaybookStats,
  PlaybookTicketPanel,
  PlaybookTimelineStats,
  PlaybookTemplateType,
  PlaybookTemplateStepDef,
  RuleSuggestion,
  RuleReviewStatus,
  StepStatus,
  HITLReviewQueueResponse,
  HITLReviewStats,
  GoldenSetEntry,
  GoldenSetRunResult,
  GoldenSetReport,
  GoldenSetCandidate,
  AutoTuneResult,
} from '../types';

export interface PlaybookRecommendationsResponse {
  session: PlaybookSession | null;
  recommendations: PlaybookRecommendation[];
  count: number;
}

export interface PlaybookSessionsResponse {
  sessions: PlaybookSession[];
  count: number;
  total: number;
}

export class PlaybooksService {
  static async getSessions(params?: {
    client_id?: string;
    alert_id?: string;
    limit?: number;
    offset?: number;
  }): Promise<PlaybookSessionsResponse> {
    try {
      const response = await api.get('/playbook/sessions', { params });
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.error || 'Failed to fetch playbook sessions');
    }
  }

  static async getRecommendationsForSession(sessionId: string): Promise<PlaybookRecommendationsResponse> {
    try {
      const response = await api.get(`/playbook/sessions/${sessionId}/recommendations`);
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.error || 'Failed to fetch recommendations');
    }
  }

  static async getRecommendationsForAlert(alertId: string): Promise<PlaybookRecommendationsResponse> {
    try {
      const response = await api.get(`/playbook/alerts/${alertId}/recommendations`);
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.error || 'Failed to fetch recommendations');
    }
  }

  static async generateForAlert(alertId: string): Promise<PlaybookRecommendationsResponse> {
    try {
      const response = await api.post(`/playbook/alerts/${alertId}/generate`);
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.error || 'Failed to generate playbook');
    }
  }

  static async updateRecommendation(
    recommendationId: string,
    outcome: string,
    actionFeedback: { action: string; decision: string }[] = [],
  ): Promise<PlaybookRecommendation> {
    try {
      const response = await api.patch(`/playbook/recommendations/${recommendationId}`, {
        outcome,
        action_feedback: actionFeedback,
      });
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.error || 'Failed to update recommendation');
    }
  }

  static async getPlaybookStats(clientId: string): Promise<PlaybookStats> {
    try {
      const response = await api.get('/playbook/stats', { params: { client_id: clientId } });
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.error || 'Failed to fetch playbook stats');
    }
  }

  static async getRecommendationsForTicket(ticketId: string): Promise<PlaybookRecommendationsResponse> {
    try {
      const response = await api.get(`/playbook/tickets/${ticketId}/recommendations`);
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.error || 'Failed to fetch recommendations');
    }
  }

  static async generateForTicket(ticketId: string): Promise<PlaybookRecommendationsResponse> {
    try {
      const response = await api.post(`/playbook/tickets/${ticketId}/generate`);
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.error || 'Failed to generate playbook for ticket');
    }
  }

  static async getSessionSteps(sessionId: string): Promise<PlaybookSessionStep[]> {
    try {
      const response = await api.get(`/playbook/sessions/${sessionId}/steps`);
      return response.data.steps;
    } catch (error: any) {
      throw new Error(error.response?.data?.error || 'Failed to fetch session steps');
    }
  }

  static async getTicketPlaybook(ticketId: string): Promise<PlaybookTicketPanel> {
    try {
      const response = await api.get(`/playbook/tickets/${ticketId}/playbook`);
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.error || 'Failed to fetch ticket playbook');
    }
  }

  static async getTimelineStats(
    clientId: string,
    period: '7d' | '30d' | '90d' = '30d',
  ): Promise<PlaybookTimelineStats> {
    try {
      const response = await api.get('/playbook/stats/timeline', { params: { client_id: clientId, period } });
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.error || 'Failed to fetch timeline stats');
    }
  }

  static async getActionsStats(clientId: string, top: number = 10): Promise<PlaybookActionsStats> {
    try {
      const response = await api.get('/playbook/stats/actions', { params: { client_id: clientId, top } });
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.error || 'Failed to fetch action stats');
    }
  }

  static async updateSessionStep(
    sessionId: string,
    stepId: string,
    data: { status?: StepStatus; notes?: string; outcome?: string | null },
  ): Promise<PlaybookSessionStep> {
    try {
      const response = await api.patch(`/playbook/sessions/${sessionId}/steps/${stepId}`, data);
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.error || 'Failed to update step');
    }
  }

  // ── Template CRUD ──────────────────────────────────────────────────────────

  static async getTemplates(clientId?: string): Promise<{ templates: PlaybookTemplateType[]; count: number }> {
    try {
      const response = await api.get('/playbook/templates', { params: clientId ? { client_id: clientId } : {} });
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.error || 'Failed to fetch templates');
    }
  }

  static async getTemplate(templateId: string): Promise<PlaybookTemplateType> {
    try {
      const response = await api.get(`/playbook/templates/${templateId}`);
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.error || 'Failed to fetch template');
    }
  }

  static async createTemplate(data: {
    type: string;
    name: string;
    version?: string;
    client_id: string;
    detection_signals?: PlaybookTemplateType['detection_signals'];
    ai_focus?: string;
    steps: Partial<PlaybookTemplateStepDef>[];
  }): Promise<PlaybookTemplateType> {
    try {
      const response = await api.post('/playbook/templates', data);
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.error || 'Failed to create template');
    }
  }

  static async updateTemplate(templateId: string, data: {
    name?: string;
    version?: string;
    detection_signals?: PlaybookTemplateType['detection_signals'];
    ai_focus?: string;
  }): Promise<PlaybookTemplateType> {
    try {
      const response = await api.patch(`/playbook/templates/${templateId}`, data);
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.error || 'Failed to update template');
    }
  }

  static async deleteTemplate(templateId: string): Promise<void> {
    try {
      await api.delete(`/playbook/templates/${templateId}`);
    } catch (error: any) {
      throw new Error(error.response?.data?.error || 'Failed to delete template');
    }
  }

  // ── Step management ────────────────────────────────────────────────────────

  static async createStep(templateId: string, data: Partial<PlaybookTemplateStepDef>): Promise<PlaybookTemplateStepDef> {
    try {
      const response = await api.post(`/playbook/templates/${templateId}/steps`, data);
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.error || 'Failed to create step');
    }
  }

  static async updateStep(templateId: string, stepId: string, data: Partial<PlaybookTemplateStepDef>): Promise<PlaybookTemplateStepDef> {
    try {
      const response = await api.patch(`/playbook/templates/${templateId}/steps/${stepId}`, data);
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.error || 'Failed to update step');
    }
  }

  static async deleteStep(templateId: string, stepId: string): Promise<void> {
    try {
      await api.delete(`/playbook/templates/${templateId}/steps/${stepId}`);
    } catch (error: any) {
      throw new Error(error.response?.data?.error || 'Failed to delete step');
    }
  }

  static async reorderSteps(templateId: string, order: string[]): Promise<{ steps: PlaybookTemplateStepDef[] }> {
    try {
      const response = await api.post(`/playbook/templates/${templateId}/steps/reorder`, { order });
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.error || 'Failed to reorder steps');
    }
  }

  // ── Rule generation & suggestions ─────────────────────────────────────────

  static async importTemplateFromFile(
    file: File,
    clientId: string,
    framework?: string,
  ): Promise<PlaybookTemplateType> {
    try {
      const form = new FormData();
      form.append('file', file);
      form.append('client_id', clientId);
      if (framework) form.append('framework', framework);
      const response = await api.post('/playbook/templates/import', form, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 240_000,
      });
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.error || 'Failed to import template');
    }
  }

  static async importTemplateFromJson(
    data: {
      name: string;
      type: string;
      version?: string;
      client_id: string;
      framework?: string;
      ai_focus?: string;
      detection_signals?: PlaybookTemplateType['detection_signals'];
      steps: Partial<PlaybookTemplateStepDef>[];
    },
  ): Promise<PlaybookTemplateType> {
    try {
      const response = await api.post('/playbook/templates/import', data);
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.error || 'Failed to import template');
    }
  }

  static async generateRules(templateId: string, clientId: string): Promise<{ message: string }> {
    try {
      const response = await api.post(`/playbook/templates/${templateId}/generate-rules`, { client_id: clientId });
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.error || 'Failed to start rule generation');
    }
  }

  static async getRuleSuggestions(templateId: string, clientId: string, status?: string): Promise<{ suggestions: RuleSuggestion[]; count: number }> {
    try {
      const response = await api.get(`/playbook/templates/${templateId}/rule-suggestions`, {
        params: { client_id: clientId, ...(status ? { status } : {}) },
      });
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.error || 'Failed to fetch rule suggestions');
    }
  }

  static async approveRuleSuggestion(suggestionId: string, ruleJson?: object): Promise<RuleSuggestion> {
    try {
      const response = await api.post(`/playbook/rule-suggestions/${suggestionId}/approve`,
        ruleJson ? { rule_json: ruleJson } : {});
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.error || 'Failed to approve suggestion');
    }
  }

  static async rejectRuleSuggestion(suggestionId: string): Promise<RuleSuggestion> {
    try {
      const response = await api.post(`/playbook/rule-suggestions/${suggestionId}/reject`);
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.error || 'Failed to reject suggestion');
    }
  }

  static async editRuleSuggestion(suggestionId: string, ruleJson: object): Promise<RuleSuggestion> {
    try {
      const response = await api.patch(`/playbook/rule-suggestions/${suggestionId}`, { rule_json: ruleJson });
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.error || 'Failed to edit suggestion');
    }
  }

  // ── Rule review (misfiring rules flagged for curator review) ────────────────

  static async getRuleReviews(clientId?: string, status?: string): Promise<{ reviews: RuleReviewStatus[]; count: number }> {
    try {
      const response = await api.get('/playbook/rules/review', {
        params: { ...(clientId ? { client_id: clientId } : {}), ...(status ? { status } : {}) },
      });
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.error || 'Failed to fetch rule reviews');
    }
  }

  static async scanRuleReviews(clientId?: string): Promise<{ scanned_clients: number; newly_flagged: number; reviews: RuleReviewStatus[] }> {
    try {
      const response = await api.post('/playbook/rules/review/scan', clientId ? { client_id: clientId } : {});
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.error || 'Failed to scan for misfiring rules');
    }
  }

  static async resolveRuleReview(reviewId: string, status: 'reviewed' | 'dismissed', note?: string): Promise<RuleReviewStatus> {
    try {
      const response = await api.post(`/playbook/rules/review/${reviewId}/resolve`, { status, ...(note ? { note } : {}) });
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.error || 'Failed to update rule review');
    }
  }

  static async getFlaggedRuleDefinition(reviewId: string): Promise<Record<string, unknown>> {
    try {
      const response = await api.get(`/playbook/rules/review/${reviewId}/rule`);
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.error || 'Failed to fetch rule definition');
    }
  }

  static async saveFlaggedRuleOverride(reviewId: string, ruleJson: object): Promise<Record<string, unknown>> {
    try {
      const response = await api.put(`/playbook/rules/review/${reviewId}/rule`, { rule_json: ruleJson });
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.error || 'Failed to save rule');
    }
  }

  // ── Template step suggestions ──────────────────────────────────────────────

  static async getTemplateStepSuggestions(
    clientId: string,
    status?: 'pending' | 'approved' | 'rejected',
  ): Promise<{ suggestions: import('../types').TemplateStepSuggestion[]; count: number }> {
    try {
      const response = await api.get('/playbook/template-step-suggestions', {
        params: { client_id: clientId, ...(status ? { status } : {}) },
      });
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.error || 'Failed to load template step suggestions');
    }
  }

  static async analyzeTemplateStepPatterns(clientId: string): Promise<void> {
    try {
      await api.post('/playbook/template-step-suggestions/analyze', { client_id: clientId });
    } catch (error: any) {
      throw new Error(error.response?.data?.error || 'Failed to start analysis');
    }
  }

  static async approveTemplateStepSuggestion(
    suggestionId: string,
  ): Promise<import('../types').TemplateStepSuggestion> {
    try {
      const response = await api.post(`/playbook/template-step-suggestions/${suggestionId}/approve`);
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.error || 'Failed to approve suggestion');
    }
  }

  static async rejectTemplateStepSuggestion(
    suggestionId: string,
  ): Promise<import('../types').TemplateStepSuggestion> {
    try {
      const response = await api.post(`/playbook/template-step-suggestions/${suggestionId}/reject`);
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.error || 'Failed to reject suggestion');
    }
  }

  // ── Session verdict ────────────────────────────────────────────────────────

  static async setSessionVerdict(
    sessionId: string,
    verdict: 'correct' | 'incorrect' | 'partial',
    notes?: string,
  ): Promise<PlaybookSession> {
    try {
      const response = await api.patch(`/playbook/sessions/${sessionId}/verdict`, {
        analyst_verdict: verdict,
        ...(notes ? { analyst_verdict_notes: notes } : {}),
      });
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.error || 'Failed to set verdict');
    }
  }

  static async setStepFeedback(
    sessionId: string,
    items: { step_index: number; decision: 'accepted' | 'skipped' }[],
  ): Promise<PlaybookSession> {
    try {
      const response = await api.post(`/playbook/sessions/${sessionId}/step-feedback`, { items });
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.error || 'Failed to save step feedback');
    }
  }

  static async escalateSession(
    sessionId: string,
    assigneeId?: string,
    note?: string,
  ): Promise<PlaybookSession> {
    try {
      const response = await api.patch(`/playbook/sessions/${sessionId}/escalate`, {
        ...(assigneeId ? { assignee_id: assigneeId } : {}),
        ...(note ? { note } : {}),
      });
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.error || 'Failed to escalate session');
    }
  }

  // ── Review Queue ───────────────────────────────────────────────────────────

  static async getReviewQueue(
    clientId?: string,
    params?: { hitl_only?: boolean; reviewed?: boolean; limit?: number; offset?: number },
  ): Promise<HITLReviewQueueResponse> {
    try {
      const response = await api.get('/playbook/review-queue', {
        params: { ...(clientId ? { client_id: clientId } : {}), ...params },
      });
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.error || 'Failed to fetch review queue');
    }
  }

  static async getReviewStats(clientId?: string, days: number = 30): Promise<HITLReviewStats> {
    try {
      const response = await api.get('/playbook/review-stats', {
        params: { ...(clientId ? { client_id: clientId } : {}), days },
      });
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.error || 'Failed to fetch review stats');
    }
  }

  // ── Auto-tune ─────────────────────────────────────────────────────────────

  static async autoTune(clientId: string): Promise<AutoTuneResult> {
    try {
      const response = await api.post('/playbook/stats/auto-tune', null, {
        params: { client_id: clientId },
      });
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.error || 'Failed to run auto-tune');
    }
  }

  // ── HITL criteria ─────────────────────────────────────────────────────────

  static async setHITLCriteria(
    clientId: string,
    criteria: {
      min_confidence?: 'none' | 'low' | 'medium' | 'high' | null;
      max_asset_criticality?: 'low' | 'medium' | 'high' | 'critical' | null;
      max_noise_warnings?: number | null;
    },
  ): Promise<void> {
    try {
      await api.put('/playbook/hitl-criteria', { client_id: clientId, hitl_criteria: criteria });
    } catch (error: any) {
      throw new Error(error.response?.data?.error || 'Failed to save HITL criteria');
    }
  }

  static async setSubnetCorrelationSettings(
    clientId: string,
    settings: { enabled: boolean; prefix_length?: number },
  ): Promise<void> {
    try {
      await api.put('/playbook/subnet-correlation-settings', { client_id: clientId, ...settings });
    } catch (error: any) {
      throw new Error(error.response?.data?.error || 'Failed to save subnet correlation settings');
    }
  }

  // ── Golden Set ────────────────────────────────────────────────────────────

  static async getGoldenSetEntries(
    clientId: string,
    params?: { session_id?: string },
  ): Promise<{ entries: GoldenSetEntry[]; count: number }> {
    try {
      const response = await api.get('/playbook/golden-set', { params: { client_id: clientId, ...params } });
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.error || 'Failed to fetch golden set');
    }
  }

  static async createGoldenSetEntry(data: {
    client_id: string;
    session_id: string;
    name?: string;
    notes?: string;
    baseline_summary?: string;
    baseline_next_steps?: { step: string; rationale: string }[];
    relevant_chunk_ids?: string[];
  }): Promise<GoldenSetEntry> {
    try {
      const response = await api.post('/playbook/golden-set', data);
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.error || 'Failed to curate session');
    }
  }

  static async deleteGoldenSetEntry(entryId: string): Promise<void> {
    try {
      await api.delete(`/playbook/golden-set/${entryId}`);
    } catch (error: any) {
      throw new Error(error.response?.data?.error || 'Failed to delete golden set entry');
    }
  }

  static async getGoldenSetCandidates(
    clientId: string,
    params?: { min_acceptance_rate?: number; limit?: number },
  ): Promise<{ candidates: GoldenSetCandidate[]; count: number }> {
    try {
      const response = await api.get('/playbook/golden-set/candidates', {
        params: { client_id: clientId, ...params },
      });
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.error || 'Failed to fetch candidates');
    }
  }

  static async getGoldenSetReport(clientId: string): Promise<GoldenSetReport> {
    try {
      const response = await api.get('/playbook/golden-set/report', {
        params: { client_id: clientId },
      });
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.error || 'Failed to fetch golden set report');
    }
  }

  static async runGoldenSetEntry(entryId: string): Promise<GoldenSetRunResult> {
    try {
      const response = await api.post(`/playbook/golden-set/${entryId}/run`);
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.error || 'Failed to run golden set entry');
    }
  }

  static async runAllGoldenSet(clientId: string): Promise<void> {
    try {
      await api.post('/playbook/golden-set/run-all', null, { params: { client_id: clientId } });
    } catch (error: any) {
      throw new Error(error.response?.data?.error || 'Failed to run all golden set');
    }
  }

  static async getGoldenSetResults(entryId: string): Promise<{ results: GoldenSetRunResult[]; count: number }> {
    try {
      const response = await api.get(`/playbook/golden-set/${entryId}/results`);
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.error || 'Failed to fetch run results');
    }
  }

  static async gradeGoldenSetResult(
    entryId: string,
    resultId: string,
    verdict: 'approved' | 'rejected',
  ): Promise<GoldenSetRunResult> {
    try {
      const response = await api.patch(`/playbook/golden-set/${entryId}/results/${resultId}`, {
        analyst_verdict: verdict,
      });
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.error || 'Failed to grade result');
    }
  }
}